import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { buildOrdatMutationVariables } from './mutationVariables.ts';
import OrdatIntegrationForm from './OrdatIntegrationForm.tsx';
import { GET_ORDAT_INTEGRATIONS, UPDATE_ORDAT_INTEGRATION } from './queries.ts';
import type { OrdatFormData, OrdatIntegration } from './types.ts';
import Button from '../../../core/components/Button.tsx';
import Layout from '../../../core/components/Layout.tsx';
import Page from '../../../core/components/Page/index.tsx';
import SettingsNavigation from '../../../core/components/SettingsNavigation.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import { url } from '../../../core/utils/link.ts';
import IntegrationBreadcrumb from '../../components/IntegrationBreadcrumb.tsx';
import { CUSTOMER_INTEGRATION_ORDAT_PATH } from '../../constants.ts';

const FORM_ID = 'ordat-integration-form';

const OrdatIntegrationEditPage = () => {
    const { t } = useTranslation('integrations');
    const { integrationId } = useParams();
    const { publishToast } = useToaster();

    const backTooltipText = `${t('backTooltipText')} ${t('ordat.title')}`;

    const { data, loading, refetch } = useQuery(GET_ORDAT_INTEGRATIONS, {
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: false,
    });
    const integration: OrdatIntegration | undefined = data?.ordatIntegrations?.find((i) => i.id === integrationId);

    const [updateIntegration, { loading: isSubmitting }] = useMutation(UPDATE_ORDAT_INTEGRATION);

    const handleUpdateIntegration = async (formData: OrdatFormData) => {
        if (!integration) return;

        try {
            await updateIntegration({
                variables: { id: integration.id, ...buildOrdatMutationVariables(formData) },
            });
            await refetch();
            publishToast({ description: t('ordat.updateSuccess'), status: 'success' });
        } catch (error) {
            console.error(error);
            publishToast({ description: t('ordat.updateError'), status: 'error' });
        }
    };

    if (!loading && !integration) {
        return (
            <Layout>
                <SettingsNavigation />
                <Page className="flex-1">
                    <Page.Content lowered>
                        <p className="text-content-medium">{t('ordat.noIntegrations')}</p>
                        <Button type="button" asChild className="mt-4">
                            <Link to={url(CUSTOMER_INTEGRATION_ORDAT_PATH)}>{t('ordat.form.cancelButton')}</Link>
                        </Button>
                    </Page.Content>
                </Page>
            </Layout>
        );
    }

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header className="justify-between">
                    <IntegrationBreadcrumb
                        backTooltip={backTooltipText}
                        items={[
                            { label: t('ordat.title'), to: url(CUSTOMER_INTEGRATION_ORDAT_PATH) },
                            { label: integration?.name || t('ordat.form.updateTitle') },
                        ]}
                    />

                    <div className="flex gap-2">
                        <Button type="button" asChild>
                            <Link to={url(CUSTOMER_INTEGRATION_ORDAT_PATH)}>{t('ordat.form.cancelButton')}</Link>
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            form={FORM_ID}
                            loading={isSubmitting}
                            disabled={loading || !integration}
                        >
                            {t('ordat.form.updateButton')}
                        </Button>
                    </div>
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6">
                    {!loading && integration && (
                        <OrdatIntegrationForm
                            integration={integration}
                            onSubmit={handleUpdateIntegration}
                            formId={FORM_ID}
                        />
                    )}
                </Page.Content>
            </Page>
        </Layout>
    );
};

export default OrdatIntegrationEditPage;
